import { useEffect, useState } from "react";
import { CreditCard, Check, ShieldAlert, Sparkles, Gift, Globe, Receipt, Download } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";

const COUNTRIES = [
  { code: "IN", label: "🇮🇳 India" }, { code: "US", label: "🇺🇸 United States" },
  { code: "AE", label: "🇦🇪 UAE" }, { code: "GB", label: "🇬🇧 United Kingdom" },
  { code: "SG", label: "🇸🇬 Singapore" }, { code: "AU", label: "🇦🇺 Australia" },
  { code: "CA", label: "🇨🇦 Canada" },
];

export default function PlansPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);
  const [country, setCountry] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [points, setPoints] = useState(0);
  const [redeem, setRedeem] = useState(0);
  const [invoices, setInvoices] = useState([]);

  const loadPlans = (c) => api.get(`/plans${c ? `?country=${c}` : ""}`).then((r) => { setPlans(r.data.plans); setCountry(r.data.country); }).catch(() => {});

  useEffect(() => {
    loadPlans();
    api.get("/subscription/me").then((r) => setSub(r.data.subscription)).catch(() => {});
    api.get("/referral").then((r) => setPoints(r.data?.stats?.points || 0)).catch(() => {});
    api.get("/invoices/me").then((r) => setInvoices(r.data.invoices)).catch(() => {});
  }, []);

  const changeCountry = async (c) => {
    setCountry(c);
    loadPlans(c);
    try { await api.put("/me/region", { country: c }); } catch (e) {}
  };

  const fmtPrice = (p) => {
    const price = p.display_price ?? p.price;
    if (price === 0) return "Free";
    const sym = p.display_symbol || "₹";
    return `${sym}${Number(price).toLocaleString()}`;
  };

  const startTrial = async (p) => {
    setBusy(p.plan_id);
    try {
      const { data } = await api.post("/subscription/trial", { plan_id: p.plan_id });
      toast.success(`${data.plan_name} trial started`, { description: `Enjoy ${data.days} days free. No payment taken.` });
      const r = await api.get("/subscription/me"); setSub(r.data.subscription);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const loadRzp = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });

  const paidPlan = plans.find((p) => (p.display_price ?? p.price) > 0);
  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    if (!paidPlan) { toast.error("No paid plan available for this region."); return; }
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim(), plan_id: paidPlan.plan_id });
      setApplied({ code: data.code, discount_type: data.discount_type, value: data.value, max_discount: data.max_discount, name: data.name });
      toast.success(`${data.code} applied`, { description: data.name });
    } catch (e) { setApplied(null); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const discountedPrice = (p) => {
    const price = p.display_price ?? p.price;
    if (price <= 0) return null;
    let disc = 0;
    if (applied) {
      disc = applied.discount_type === "percentage" ? price * applied.value / 100 : applied.value;
      if (applied.max_discount) disc = Math.min(disc, applied.max_discount);
    }
    disc = Math.min(disc, price);
    const afterCoupon = price - disc;
    const pts = Math.min(Number(redeem) || 0, points, afterCoupon);
    if (!applied && pts <= 0) return null;
    return Math.max(0, Math.round(afterCoupon - pts));
  };

  const choose = async (p) => {
    setBusy(p.plan_id);
    try {
      const { data } = await api.post("/subscription/order", { plan_id: p.plan_id, coupon_code: applied?.code, points_redeem: Math.min(Number(redeem) || 0, points) });
      if (!data.configured) {
        toast.error(data.message, { description: applied ? `${applied.code} would apply at checkout.` : "Add your Razorpay keys to enable subscription payments." });
        return;
      }
      const ok = await loadRzp();
      if (!ok) { toast.error("Couldn't load the payment window. Please try again."); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency,
        name: "Suffle Online", description: `${data.plan_name} subscription`,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone || "" },
        theme: { color: "#7C3AED" },
        handler: async (resp) => {
          try {
            await api.post("/subscription/verify", { ...resp, plan_id: p.plan_id });
            toast.success("Subscription activated!");
            const r = await api.get("/subscription/me"); setSub(r.data.subscription);
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader title="My Subscription & Plans" icon={CreditCard}
        subtitle="Pricing is tailored to your region. Suffle's payment gateway is used only for Suffle subscriptions."
        actions={
          <div className="flex items-center gap-2" data-testid="region-switcher">
            <Globe className="h-4 w-4 text-slate-400" />
            <Select value={country || undefined} onValueChange={changeCountry}>
              <SelectTrigger className="h-10 w-[180px]" data-testid="region-select"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        } />

      {sub ? (
        <div className="card-soft mb-5 flex items-center gap-3 p-4" data-testid="active-sub">
          <Sparkles className="h-5 w-5 text-emerald-500" />
          <div><p className="font-semibold text-slate-800 flex items-center gap-2">Active plan: {sub.plan_name}
            {sub.activation_type && sub.activation_type !== "Free Trial" && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600" data-testid="sub-activation-type">{sub.activation_type}</span>}</p>
            <p className="text-xs text-slate-500">Status: {sub.status}{sub.trial_ends ? ` · trial ends ${fmtDate(sub.trial_ends)}` : ""}{sub.ends ? ` · valid till ${fmtDate(sub.ends)}` : ""}{sub.source === "manual" ? " · activated by Suffle (no payment taken)" : ""}</p></div>
        </div>
      ) : (
        <div className="card-soft mb-5 flex items-center gap-3 p-4">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">Free plan</span>
          <p className="text-sm text-slate-500">You're on the Free plan. Start a free trial or upgrade any time.</p>
        </div>
      )}

      {invoices.length > 0 && (
        <div className="card-soft mb-5 p-4" data-testid="my-invoices">
          <div className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><Receipt className="h-4 w-4 text-blue-500" /> Subscription Invoices</div>
          <div className="space-y-1.5">
            {invoices.map((iv) => (
              <div key={iv.invoice_id} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid="invoice-row">
                <span className="font-medium text-slate-700">{iv.invoice_number}</span>
                {iv.invoice_date && <span className="text-xs text-slate-400">{fmtDate(iv.invoice_date)}</span>}
                <span className="text-xs text-slate-500">{iv.currency} {iv.amount}</span>
                {iv.billing_period && <span className="text-xs text-slate-400">· {iv.billing_period}</span>}
                <a href={`${API}/invoices/${iv.invoice_id}/file`} target="_blank" rel="noreferrer noopener noreferrer" className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:border-blue-300" data-testid={`my-invoice-dl-${iv.invoice_id}`}><Download className="h-3 w-3" /> Download</a>
              </div>
            ))}
          </div>
        </div>
      )}

      {!sub && paidPlan && (
        <div className="card-soft mb-5 flex flex-wrap items-center gap-2 p-4" data-testid="coupon-bar">
          <span className="text-sm font-medium text-slate-600">Have a promo code?</span>
          <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="e.g. WELCOMEBACK20" data-testid="coupon-input"
            className="h-10 flex-1 min-w-[160px] rounded-full border border-slate-200 px-4 text-sm outline-none focus:border-indigo-400" />
          <button onClick={applyCoupon} data-testid="coupon-apply" className="rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">Apply</button>
          {applied && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600" data-testid="coupon-applied">{applied.code} applied — {applied.discount_type === "percentage" ? `${applied.value}% off` : `₹${applied.value} off`} <button onClick={() => { setApplied(null); setCoupon(""); }} className="ml-1 text-emerald-400 hover:text-emerald-600">✕</button></span>}
          {points > 0 && (
            <div className="flex w-full items-center gap-2 border-t border-slate-100 pt-3" data-testid="points-redeem">
              <span className="text-sm font-medium text-slate-600">Redeem points ({points.toLocaleString()} available · 1 pt = ₹1)</span>
              <input type="number" min="0" max={points} value={redeem} onChange={(e) => setRedeem(Math.max(0, Math.min(points, Number(e.target.value) || 0)))} data-testid="points-input"
                className="h-10 w-32 rounded-full border border-slate-200 px-4 text-sm outline-none focus:border-indigo-400" />
              <button onClick={() => setRedeem(points)} data-testid="points-max" className="rounded-full border border-indigo-300 px-4 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Use max</button>
              {redeem > 0 && <button onClick={() => setRedeem(0)} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((p, i) => {
          const isFree = (p.display_price ?? p.price) === 0;
          const hasTrial = !sub && !isFree && (p.trial_days || 0) > 0;
          return (
            <motion.div key={p.plan_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="card-soft card-hover flex flex-col p-6" data-testid={`plan-${p.tier}`}>
              {p.promo && !isFree && (
                <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-2.5 py-0.5 text-[10px] font-semibold text-white" data-testid={`plan-promo-${p.tier}`}><Gift className="h-3 w-3" /> {p.promo}</span>
              )}
              <h3 className="font-brand text-lg font-bold text-slate-800">{p.name}</h3>
              {(() => { const dp = discountedPrice(p); return (
                <p className="mt-2 text-3xl font-extrabold text-slate-900" data-testid={`plan-price-${p.tier}`}>
                  {dp !== null ? <><span className="mr-2 text-lg font-medium text-slate-400 line-through">{fmtPrice(p)}</span>{p.display_symbol || "₹"}{dp.toLocaleString()}</> : fmtPrice(p)}
                  <span className="text-sm font-normal text-slate-400">{isFree ? "" : "/mo"}</span>
                </p>
              ); })()}
              <ul className="mt-4 flex-1 space-y-2">
                {(p.features || []).map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>)}
              </ul>
              {hasTrial && (
                <button onClick={() => startTrial(p)} disabled={busy === p.plan_id} data-testid={`trial-${p.tier}`}
                  className="mt-4 rounded-full border border-indigo-300 py-2.5 text-sm font-semibold text-indigo-600 transition hover:bg-indigo-50 disabled:opacity-50">
                  Start {p.trial_days}-day free trial
                </button>
              )}
              <button onClick={() => choose(p)} disabled={busy === p.plan_id || isFree} data-testid={`choose-${p.tier}`}
                className="mt-2 rounded-full brand-gradient-bg py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                {isFree ? "Current / Free" : busy === p.plan_id ? "Please wait..." : "Choose Plan"}
              </button>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        Suffle's payment gateway is only for Suffle subscriptions — never for flights, hotels, bills or third-party services. Those are always paid directly with the provider. A subscription is activated only after a verified payment; free trials never take payment.
      </div>
    </div>
  );
}
